import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useLayout } from "../../../contexts/LayoutContext";
import { useAuth } from "../../../contexts/AuthContext";
import ThemeToggle from "../../../components/ui/ThemeToggle/ThemeToggle";
import logo from "../../../assets/logo/logo.png";
import {
  Home,
  BookOpen,
  GraduationCap,
  Briefcase,
  Bell,
  LogIn,
  UserPlus,
  LogOut,
  AlertCircle,
  Check,
  ChevronRight,
  Inbox,
  User,
  X,
  CheckCheck,
  Globe,
  EyeOff,
  ShoppingBag,
  Truck,
  CreditCard,
  Tag,
  Info,
  Package,
  Mail,
  FileText,
} from "lucide-react";
import { t as i18nT } from "../../../i18n";
import api from "../../../api/client";
import { showSystemToast } from "../NotificationToast";
import { resolveFeatureSections } from "../../../utils/featureSections";
import { toAbsoluteUrl } from "../../../utils/imageUtils";
import "./Navbar.css";
import logger from "../../../utils/logger.js";

// ============================================
// Unified notification helpers (from Notifications.js)
// ============================================

function timeAgo(dateString, lang) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (lang === "ar") {
      if (diffDays > 30) return formatDate(dateString, "ar");
      if (diffDays > 0) {
        if (diffDays === 1) return "منذ يوم";
        if (diffDays === 2) return "منذ يومين";
        if (diffDays >= 3 && diffDays <= 10) return `منذ ${diffDays} أيام`;
        return `منذ ${diffDays} يومًا`;
      }
      if (diffHours > 0) {
        if (diffHours === 1) return "منذ ساعة";
        if (diffHours === 2) return "منذ ساعتين";
        if (diffHours >= 3 && diffHours <= 10) return `منذ ${diffHours} ساعات`;
        return `منذ ${diffHours} ساعة`;
      }
      if (diffMins > 0) {
        if (diffMins === 1) return "منذ دقيقة";
        if (diffMins === 2) return "منذ دقيقتين";
        if (diffMins >= 3 && diffMins <= 10) return `منذ ${diffMins} دقائق`;
        return `منذ ${diffMins} دقيقة`;
      }
      return "منذ قليل";
    } else {
      if (diffDays > 30) return formatDate(dateString, "en");
      if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
      return "Just now";
    }
  } catch {
    return dateString;
  }
}

function formatDate(dateString, lang) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    if (lang === "ar") {
      return date.toLocaleDateString("ar-SA", {
        year: "numeric", month: "long", day: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

// ============================================
// Notification type config (icon + color)
// ============================================
const NAVBAR_NOTIF_CONFIG = {
  order: { icon: ShoppingBag, color: "#f59e0b" },
  order_status: { icon: ShoppingBag, color: "#f59e0b" },
  order_status_update: { icon: ShoppingBag, color: "#f59e0b" },
  order_created: { icon: ShoppingBag, color: "#f59e0b" },
  order_placed: { icon: ShoppingBag, color: "#f59e0b" },
  order_completed: { icon: ShoppingBag, color: "#f59e0b" },
  checkout: { icon: ShoppingBag, color: "#f59e0b" },
  checkout_success: { icon: ShoppingBag, color: "#f59e0b" },
  shipment: { icon: Truck, color: "#2563eb" },
  shipping: { icon: Truck, color: "#2563eb" },
  shipping_update: { icon: Truck, color: "#2563eb" },
  shipping_status: { icon: Truck, color: "#2563eb" },
  shipping_status_update: { icon: Truck, color: "#2563eb" },
  payment: { icon: CreditCard, color: "#16a34a" },
  payment_success: { icon: CreditCard, color: "#16a34a" },
  payment_status: { icon: CreditCard, color: "#16a34a" },
  payment_failed: { icon: CreditCard, color: "#ef4444" },
  payment_error: { icon: CreditCard, color: "#ef4444" },
  invoice: { icon: FileText, color: "#2563eb" },
  invoice_created: { icon: FileText, color: "#2563eb" },
  invoice_paid: { icon: FileText, color: "#16a34a" },
  promo: { icon: Tag, color: "#b87d0a" },
  offer: { icon: Tag, color: "#b87d0a" },
  discount: { icon: Tag, color: "#b87d0a" },
  system: { icon: Info, color: "#7c3aed" },
  info: { icon: Info, color: "#2563eb" },
  warning: { icon: Info, color: "#ef4444" },
  maintenance: { icon: Info, color: "#7c3aed" },
  update: { icon: Info, color: "#7c3aed" },
  digital: { icon: Package, color: "#7c3aed" },
  download_ready: { icon: Package, color: "#7c3aed" },
  user: { icon: User, color: "#16a34a" },
  welcome: { icon: User, color: "#16a34a" },
  profile_update: { icon: User, color: "#16a34a" },
  email: { icon: Mail, color: "#2563eb" },
  verification: { icon: Mail, color: "#2563eb" },
  reset_password: { icon: Mail, color: "#2563eb" },
  service_message: { icon: Mail, color: "#16a34a" },
  freelancer_service_review: { icon: CheckCheck, color: "#2563eb" },
  consultation_review: { icon: CheckCheck, color: "#2563eb" },
  course_review: { icon: CheckCheck, color: "#2563eb" },
  job_review: { icon: CheckCheck, color: "#2563eb" },
  seller_product_review: { icon: CheckCheck, color: "#2563eb" },
  provider_profile_review: { icon: CheckCheck, color: "#2563eb" },
  default: { icon: Bell, color: "#f59e0b" },
};

function translateNotification(notification, lang) {
  const needsDownloadLink = ["digital", "download_ready"].includes(notification.type);
  const hasLink = notification.link || notification.link_url;

  if (lang !== "ar") {
    if (needsDownloadLink && !hasLink) {
      return { ...notification, link: "/my-downloads" };
    }
    return notification;
  }

  const title = notification.title || "";
  const message = notification.message || "";

  let tTitle = title
    .replace(/New message from admin/, "رسالة جديدة من الإدارة")
    .replace(/You have a new message about order #(.*)/, "لديك رسالة جديدة بخصوص الطلب #$1")
    .replace(/New order #(.*)/, "طلب جديد #$1")
    .replace(/Payment received for order #(.*)/, "تم استلام الدفع للطلب #$1")
    .replace(/Invoice (.*) is ready/, "الفاتورة $1 جاهزة")
    .replace(/Shipping update for order #(.*)/, "تحديث الشحن للطلب #$1")
    .replace(/Order #(.*) shipped/, "تم شحن الطلب #$1")
    .replace(/Order #(.*) delivered/, "تم تسليم الطلب #$1")
    .replace(/Order #(.*) is completed/, "تم إكمال الطلب #$1")
    .replace(/Order #(.*) completed/, "تم إكمال الطلب #$1")
    .replace(/Course enrollment confirmed/, "تم تأكيد التسجيل في الكورس")
    .replace(/Consultation booking confirmed/, "تم تأكيد حجز الاستشارة")
    .replace(/Your digital items are ready/, i18nT(lang, "notif_digital_ready", "عناصرك الرقمية جاهزة"))
    .replace(/Your download is ready/, i18nT(lang, "notif_download_ready", "تحميلك جاهز"))
    .replace(/Welcome to Mousa Group/, i18nT(lang, "notif_welcome", "مرحباً بك في مجموعة موسى"))
    .replace(/Your profile has been updated/, i18nT(lang, "notif_profile_update", "تم تحديث ملفك الشخصي"))
    .replace(/Please verify your email address/, i18nT(lang, "notif_email_verify", "يرجى التحقق من بريدك الإلكتروني"))
    .replace(/Your email has been verified/, i18nT(lang, "notif_email_verified", "تم التحقق من بريدك الإلكتروني"))
    .replace(/Password reset link sent/, i18nT(lang, "notif_password_reset", "تم إرسال رابط إعادة تعيين كلمة المرور"))
    .replace(/System maintenance scheduled/, i18nT(lang, "notif_maintenance", "صيانة النظام مجدولة"))
    .replace(/System update available/, i18nT(lang, "notif_system_update", "تحديث النظام متاح"))
    .replace(/New login detected/, i18nT(lang, "notif_new_login", "تم اكتشاف تسجيل دخول جديد"))
    .replace(/Your password has been changed/, i18nT(lang, "notif_password_changed", "تم تغيير كلمة المرور الخاصة بك"))
    .replace(/Service approved/, "تمت الموافقة على الخدمة")
    .replace(/Service rejected/, "تم رفض الخدمة")
    .replace(/Consultation approved/, "تمت الموافقة على الاستشارة")
    .replace(/Consultation rejected/, "تم رفض الاستشارة")
    .replace(/Course approved/, "تمت الموافقة على الكورس")
    .replace(/Course rejected/, "تم رفض الكورس")
    .replace(/Job approved/, "تمت الموافقة على الوظيفة")
    .replace(/Job rejected/, "تم رفض الوظيفة")
    .replace(/Seller product approved/, "تمت الموافقة على المنتج")
    .replace(/Seller product rejected/, "تم رفض المنتج")
    .replace(/Provider application approved/, "تمت الموافقة على طلب مقدم الخدمة")
    .replace(/Provider application rejected/, "تم رفض طلب مقدم الخدمة");

  let tMessage = message
    .replace(/You have a new message about order #(.*)/, "لديك رسالة جديدة بخصوص الطلب #$1")
    .replace(/Your order #(.*) has been created successfully/, "تم إنشاء طلبك #$1 بنجاح")
    .replace(/Your order #(.*) is completed\. Download links are now available/, "تم إكمال طلبك #$1. روابط التحميل متاحة الآن")
    .replace(/Your order #(.*) is completed/, "تم إكمال طلبك #$1")
    .replace(/Your order #(.*) has been completed/, "تم إكمال طلبك #$1")
    .replace(/Order #(.*) is completed/, "تم إكمال الطلب #$1")
    .replace(/Your payment was confirmed and you are now enrolled in (.*)/, "تم تأكيد دفعتك وأنت الآن مسجل في $1")
    .replace(/Your payment was confirmed and your booking for (.*) is now confirmed/, "تم تأكيد دفعتك وتم تأكيد حجزك لـ $1")
    .replace(/Download links are now available/, "روابط التحميل متاحة الآن")
    .replace(/Download link is now available/, "رابط التحميل متاح الآن")
    .replace(/Shipping:\s*(.*)/, "الشحن: $1")
    .replace(/Pickup from store/, "الاستلام من المتجر")
    .replace(/Home delivery/, "التوصيل للمنزل")
    .replace(/ETA:\s*(.*)/, "الوقت المتوقع: $1")
    .replace(/Same Day/, "نفس اليوم")
    .replace(/Tracking code:\s*(.*)/, "رمز التتبع: $1")
    .replace(/Your payment of \$(.*) via (.*) was completed successfully/, "تم إتمام دفعتك بقيمة $$1 عبر $2 بنجاح")
    .replace(/Payment of \$(.*) received/, "تم استلام دفعة بقيمة $$1")
    .replace(/Payment received:\s*\$(.*)/, "تم استلام دفعة بقيمة $$1")
    .replace(/Invoice (.*) is now available for download/, "الفاتورة $1 متاحة للتحميل الآن")
    .replace(/You can download your invoice from your account/, "يمكنك تحميل فاتورتك من حسابك")
    .replace(/Your invoice (.*) has been generated/, "تم إنشاء فاتورتك $1")
    .replace(/was approved and is now visible to users\./, "تمت الموافقة عليه وهو ظاهر الآن للمستخدمين.")
    .replace(/was rejected by the admin team\./, "تم رفضه من فريق الإدارة.")
    .replace(/is pending admin review\./, "بانتظار مراجعة الإدارة.")
    .replace(/Your (.*) provider application was approved\./, "تمت الموافقة على طلب مقدم الخدمة الخاص بك: $1.")
    .replace(/Your (.*) provider application was rejected\./, "تم رفض طلب مقدم الخدمة الخاص بك: $1.")
    .replace(/Your product \"(.*)\" has been approved and is now eligible to appear in the store\./, "تمت الموافقة على المنتج \"$1\" وأصبح مؤهلاً للظهور في المتجر.")
    .replace(/Your product \"(.*)\" was rejected by the admin\./, "تم رفض المنتج \"$1\" من الإدارة.");

  const systemMsgPatterns = [
    { en: /You can now download your files from your account/, ar: i18nT(lang, "notif_download_account", "يمكنك الآن تحميل ملفاتك من حسابك") },
    { en: /Click here to access your downloads/, ar: i18nT(lang, "notif_download_link_text", "اضغط هنا للوصول إلى التحميلات") },
    { en: /Your digital items are ready for download/, ar: "عناصرك الرقمية جاهزة للتحميل" },
    { en: /Your files are now available in your downloads section/, ar: "ملفاتك متاحة الآن في قسم التحميلات" },
    { en: /Your download is ready\. Click to access/, ar: "تحميلك جاهز. اضغط للوصول" },
    { en: /Welcome to Mousa Group\. We're glad to have you/, ar: "مرحباً بك في مجموعة موسى. نحن سعداء بانضمامك" },
    { en: /Your profile information has been updated successfully/, ar: "تم تحديث معلومات ملفك الشخصي بنجاح" },
    { en: /Please verify your email by clicking the link sent to/, ar: "يرجى التحقق من بريدك الإلكتروني بالضغط على الرابط المرسل إلى" },
    { en: /Your email has been successfully verified/, ar: "تم التحقق من بريدك الإلكتروني بنجاح" },
    { en: /A password reset link has been sent to your email/, ar: "تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني" },
    { en: /Scheduled maintenance will occur on/, ar: "صيانة مجدولة ستتم في" },
    { en: /A new system update is available/, ar: "تحديث جديد للنظام متاح" },
    { en: /We detected a new login to your account from/, ar: "اكتشفنا تسجيل دخول جديد إلى حسابك من" },
    { en: /Your password was successfully changed/, ar: "تم تغيير كلمة المرور الخاصة بك بنجاح" },
    { en: /If this wasn't you, please contact support immediately/, ar: "إذا لم تكن أنت، يرجى الاتصال بالدعم فوراً" },
  ];

  systemMsgPatterns.forEach(({ en, ar }) => {
    tMessage = tMessage.replace(en, ar);
  });

  const translated = { ...notification, title: tTitle, message: tMessage };

  if (needsDownloadLink && !translated.link && !translated.link_url) {
    translated.link = "/my-downloads";
  }

  return translated;
}

// ============================================
// Notification dismiss — localStorage fallback only
// Primary source of truth is now the server (is_dismissed field)
// ============================================

const NAVBAR_DISMISSED_KEY = "mg_dismissed_notifications";

function saveNavbarDismissedId(id, allServerIds = []) {
  try {
    const existing = new Set(
      JSON.parse(localStorage.getItem(NAVBAR_DISMISSED_KEY) || "[]")
    );
    existing.add(String(id));
    const serverSet = new Set(allServerIds.map(String));
    const pruned = [...existing].filter((sid) => serverSet.has(sid));
    localStorage.setItem(NAVBAR_DISMISSED_KEY, JSON.stringify(pruned));
  } catch {}
}

// ============================================
// دالة موحدة لحساب العدد من localStorage
// ============================================

// ============================================
// المكون الرئيسي
// ============================================

export default function Navbar() {
  const { lang, toggleLanguage, t } = useLanguage();
  const { settings } = useLayout();
  const { user, logout, isLogged, isVerified } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [navAvatarError, setNavAvatarError] = useState(false);
  const rawNavAvatar = user?.avatar_url || "";
  const navAvatarUrl = /^(https?:\/\/|blob:|data:)/i.test(rawNavAvatar)
    ? rawNavAvatar
    : toAbsoluteUrl(rawNavAvatar);

  useEffect(() => {
    setNavAvatarError(false);
  }, [navAvatarUrl]);

  const showNavAvatar = Boolean(navAvatarUrl) && !navAvatarError;

  const isAr = lang === "ar";
  const sections = resolveFeatureSections(settings);
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuTop, setMenuTop] = useState(52);


  const toggleMenu = () => setIsOpen((p) => !p);
  const closeMenu = () => setIsOpen(false);
  const showBanner = isLogged && !isVerified;

  const [verifySending, setVerifySending] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");

  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileNotifOpen, setMobileNotifOpen] = useState(false);

  const [allNotifications, setAllNotifications] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationSoundRef = useRef(null);
  const notifDropdownRef = useRef(null);
  const mobileNotifRef = useRef(null);
  const lastToastIdRef = useRef(null);
  const echoRef = useRef(null);
  const allNotifIds = useRef([]);

  const tx = useCallback(
    (key, arFallback, enFallback) => {
      const translated = i18nT(lang, key);
      if (translated && translated !== key) return translated;
      return lang === "ar" ? arFallback : enFallback;
    },
    [lang]
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setNotifOpen(false);
    setMobileNotifOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const calculateTop = () => {
      const navHeight = window.innerWidth <= 576 ? 48 : 52;
      const bannerHeight = showBanner ? 42 : 0;
      return navHeight + bannerHeight;
    };
    setMenuTop(calculateTop());
    const handleResize = () => setMenuTop(calculateTop());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [showBanner]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [isOpen]);

  const handleResendVerification = async () => {
    if (!isLogged) return;
    setVerifyMsg("");
    setVerifySending(true);
    try {
      const email = user?.email || "";
      if (!email) {
        setVerifyMsg(isAr ? "البريد غير متوفر." : "Email not found.");
        return;
      }
      const res = await api.apiPost("resend-verification", { email });
      setVerifyMsg(
        res?.message || (isAr ? "تم إرسال رسالة التفعيل." : "Activation email sent.")
      );
    } catch (e) {
      setVerifyMsg(e?.message || (isAr ? "حصل خطأ أثناء الإرسال." : "Error while sending."));
    } finally {
      setVerifySending(false);
    }
  };

  const normalizeNotificationsList = (res) => {
    const list = res?.notifications ?? res?.data?.notifications ?? res?.data ?? res;
    const normalized = Array.isArray(list) ? list : [];

    // Fix service_message notification links: /my-service-orders/{id} → /service-orders/{id}
    return normalized.map((n) => {
      if (n?.type === 'service_message' && n?.link_url?.includes('/my-service-orders/')) {
        return { ...n, link_url: n.link_url.replace('/my-service-orders/', '/service-orders/') };
      }
      return n;
    });
  };

  const normalizeUnreadCount = (res) =>
    Number(
      res?.unread_count ?? res?.data?.unread_count ?? res?.count ?? res?.data?.count ?? res?.data ?? 0
    );

  const getStoredAuthToken = () => {
    const candidates = [
      localStorage.getItem("mg_token"), localStorage.getItem("token"),
      localStorage.getItem("auth_token"), localStorage.getItem("access_token"),
      localStorage.getItem("jwt"), sessionStorage.getItem("mg_token"),
      sessionStorage.getItem("token"), sessionStorage.getItem("auth_token"),
      sessionStorage.getItem("access_token"), sessionStorage.getItem("jwt"),
    ];
    const token = candidates.find((v) => typeof v === "string" && v.trim() !== "");
    return token ? token.trim() : "";
  };

  const playNotificationSound = () => {
    const audio = notificationSoundRef.current;
    if (!audio) return;
    try {
      audio.currentTime = 0;
      audio.play().catch(() => {});
    } catch {}
  };

  const showToastFromNotification = useCallback(
    (incoming) => {
      if (!incoming || typeof incoming !== "object") return;
      const uniqueKey = incoming.__toastKey || incoming.id || `${incoming.type || "notification"}-${Date.now()}`;
      const toastPayload = { ...incoming, __toastKey: String(uniqueKey) };
      playNotificationSound();
      showSystemToast(toastPayload, isAr);
    },
    [isAr]
  );

  const pushRealtimeNotification = useCallback(
    (incoming) => {
      if (!incoming || typeof incoming !== "object") return;

      // Fix service_message notification links
      let notification = incoming;
      if (notification.type === 'service_message' && notification.link_url?.includes('/my-service-orders/')) {
        notification = { ...notification, link_url: notification.link_url.replace('/my-service-orders/', '/service-orders/') };
      }

      // Debug approval/review notifications end-to-end from broadcast payload
      // to bell state, especially after the backend UUID notification fixes.
      logger.debug("Navbar realtime notification received", {
        id: notification?.id || null,
        type: notification?.type || "",
        isRead: notification?.is_read ?? null,
      });

      const incomingId = notification.id ?? null;
      setAllNotifications((prev) => {
        const existing = Array.isArray(prev) ? prev : [];
        const filtered = incomingId ? existing.filter((item) => item?.id !== incomingId) : existing;
        const next = [notification, ...filtered].slice(0, 50);
        setNotifications(next.slice(0, 5));
        return next;
      });
      setUnreadCount((prev) => Number(prev || 0) + (notification.is_read ? 0 : 1));
      if (incomingId) lastToastIdRef.current = incomingId;
      showToastFromNotification(notification);
    },
    [showToastFromNotification]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifDropdownRef.current && !notifDropdownRef.current.contains(event.target)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileNotifRef.current && !mobileNotifRef.current.contains(event.target)) {
        setMobileNotifOpen(false);
      }
    };
    if (mobileNotifOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileNotifOpen]);

  useEffect(() => {
    function unlockSoundOnce() {
      const audioEl = notificationSoundRef.current;
      if (!audioEl) return;
      audioEl.play().then(() => { audioEl.pause(); audioEl.currentTime = 0; }).catch(() => {});
      document.removeEventListener("click", unlockSoundOnce);
    }
    document.addEventListener("click", unlockSoundOnce);
    return () => document.removeEventListener("click", unlockSoundOnce);
  }, []);

  useEffect(() => {
    if (!isLogged) {
      setAllNotifications([]);
      setNotifications([]);
      setUnreadCount(0);
      setNotifLoading(false);
      lastToastIdRef.current = null;
      return;
    }
    let cancelled = false;
    let manualRefreshTimeout = null;
    const safeApiCall = async (fn) => {
      try { return await fn(); }
      catch (err) { return { status: "error", message: err?.message || "Request failed" }; }
    };
    async function loadNotifications(showToast = false) {
      try {
        setNotifLoading(true);
        const [listData, countData] = await Promise.all([
          safeApiCall(() => api.apiGet(`notifications?limit=50&lang=${encodeURIComponent(lang)}&_t=` + Date.now())),
          safeApiCall(() => api.apiGet(`notifications/unread-count?lang=${encodeURIComponent(lang)}&_t=` + Date.now())),
        ]);
        if (cancelled) return;
        const list = normalizeNotificationsList(listData);
        // Helps verify the account bell is receiving real server rows and not
        // silently dropping review notifications due to malformed IDs or types.
        logger.debug("Navbar notifications fetched", {
          total: Array.isArray(list) ? list.length : 0,
          sampleTypes: Array.isArray(list) ? list.slice(0, 5).map((item) => item?.type || "") : [],
          showToast,
        });
        allNotifIds.current = list.map((n) => n?.id).filter(Boolean);
        let newUnreadCount;
        if (countData && !String(countData.status || "").includes("error")) {
          newUnreadCount = normalizeUnreadCount(countData);
        } else {
          newUnreadCount = list.filter((n) => !n.is_read).length;
        }
        // Compare unread-count endpoint behavior with the fetched list so we can
        // spot mismatches between persistence, API serialization, and bell state.
        logger.debug("Navbar notifications unread count resolved", {
          unreadCount: newUnreadCount,
          source: countData && !String(countData.status || "").includes("error") ? "api" : "list-fallback",
        });
        setAllNotifications(list);
        setNotifications(list.slice(0, 5));
        setUnreadCount(newUnreadCount);
        if (showToast && list.length > 0) {
          const unreadItems = list.filter((n) => Number(n?.is_read ?? 0) === 0);
          for (const item of unreadItems.slice().reverse()) {
            const itemId = item?.id ?? null;
            if (itemId && itemId !== lastToastIdRef.current) {
              lastToastIdRef.current = itemId;
              showToastFromNotification(item);
            }
          }
        } else if (list.length > 0) {
          lastToastIdRef.current = list[0]?.id ?? null;
        }
      } finally {
        if (!cancelled) setNotifLoading(false);
      }
    }
    loadNotifications(false);
    const handleManualRefresh = () => {
      if (manualRefreshTimeout) clearTimeout(manualRefreshTimeout);
      manualRefreshTimeout = setTimeout(() => loadNotifications(true), 300);
    };
    window.addEventListener("mg:notifications-refresh", handleManualRefresh);
    return () => {
      cancelled = true;
      if (manualRefreshTimeout) clearTimeout(manualRefreshTimeout);
      window.removeEventListener("mg:notifications-refresh", handleManualRefresh);
    };
  }, [isLogged, lang, showToastFromNotification]);

  useEffect(() => {
    const handleDismissed = (e) => {
      const { id } = e.detail;
      setAllNotifications((prev) => {
        const filtered = prev.filter((n) => n.id !== id);
        setNotifications(filtered.slice(0, 5));
        return filtered;
      });
    };
    const handleDismissedAll = () => {
      setAllNotifications([]);
      setNotifications([]);
      setUnreadCount(0);
    };
    const handleMarkedRead = (e) => {
      const { id } = e.detail;
      setAllNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    };
    const handleMarkedAllRead = () => {
      setAllNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    };
    window.addEventListener("mg:notification-dismissed", handleDismissed);
    window.addEventListener("mg:notifications-dismissed-all", handleDismissedAll);
    window.addEventListener("mg:notification-marked-read", handleMarkedRead);
    window.addEventListener("mg:notifications-marked-all-read", handleMarkedAllRead);
    return () => {
      window.removeEventListener("mg:notification-dismissed", handleDismissed);
      window.removeEventListener("mg:notifications-dismissed-all", handleDismissedAll);
      window.removeEventListener("mg:notification-marked-read", handleMarkedRead);
      window.removeEventListener("mg:notifications-marked-all-read", handleMarkedAllRead);
    };
  }, []);

  useEffect(() => {
    if (!isLogged || !user?.id) {
      if (echoRef.current) {
        try { echoRef.current.disconnect(); } catch {}
        echoRef.current = null;
      }
      return;
    }
    const token = getStoredAuthToken();
    if (!token) {
      logger.warn("Realtime notifications skipped: missing auth token");
      return;
    }
    try {
      window.Pusher = Pusher;
      const isHttps = window.location.protocol === "https:";
      const wsHost = import.meta.env.VITE_REVERB_HOST || import.meta.env.VITE_PUSHER_HOST || window.location.hostname;
      const wsPort = Number(import.meta.env.VITE_REVERB_PORT || import.meta.env.VITE_PUSHER_PORT || 6001);
      const wssPort = Number(import.meta.env.VITE_REVERB_PORT || import.meta.env.VITE_PUSHER_PORT || 443);
      const appKey = import.meta.env.VITE_REVERB_APP_KEY || import.meta.env.VITE_PUSHER_APP_KEY || "app-key";
      const authEndpoint = `${window.location.origin}/laravel-api/broadcasting/auth`;
      const echo = new Echo({
        broadcaster: "pusher", key: appKey, wsHost, wsPort, wssPort,
        forceTLS: isHttps, encrypted: isHttps, disableStats: true,
        enabledTransports: ["ws", "wss"], authEndpoint,
        auth: { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } },
      });
      echoRef.current = echo;
      echo.private(`user.${user.id}`).listen(".notification.created", (payload) => {
        const incoming = payload?.notification ?? payload?.data ?? payload;
        pushRealtimeNotification(incoming);
      });
    } catch (err) {
      logger.error("Realtime notification connection failed", err);
    }
    return () => {
      try {
        if (echoRef.current) {
          echoRef.current.leave(`private-user.${user.id}`);
          echoRef.current.disconnect();
        }
      } catch {}
      echoRef.current = null;
    };
  }, [isLogged, user?.id, pushRealtimeNotification]);

  const handleOpenNotifications = () => {
    if (!isLogged) return;
    setNotifOpen((v) => !v);
  };

  const handleOpenMobileNotifications = () => {
    if (!isLogged) return;
    setMobileNotifOpen((v) => !v);
  };

  const handleMarkAsRead = (notificationId, e) => {
    if (e) e.stopPropagation();
    setAllNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n)));
    setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    window.dispatchEvent(new CustomEvent("mg:notification-marked-read", { detail: { id: notificationId } }));
    api.apiPost(`notifications/${notificationId}/mark-read`, {}).catch(() => {});
  };

  const handleNotificationClick = (rawN) => {
    const n = translateNotification(rawN, lang);
    if (!n.is_read) {
      setAllNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, is_read: 1 } : item)));
      setNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, is_read: 1 } : item)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      window.dispatchEvent(new CustomEvent("mg:notification-marked-read", { detail: { id: n.id } }));
      api.apiPost(`notifications/${n.id}/mark-read`, {}).catch(() => {});
    }
    const targetLink = n.link_url || n.link;
    if (targetLink) {
      if (String(targetLink).startsWith("http")) {
        window.open(targetLink, "_blank");
      } else {
        navigate(targetLink);
      }
    } else {
      navigate("/account/notifications");
    }
    setNotifOpen(false);
    setMobileNotifOpen(false);
  };

  const handleMarkAllRead = () => {
    setAllNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    setUnreadCount(0);
    window.dispatchEvent(new CustomEvent("mg:notifications-marked-all-read"));
    api.apiPost("notifications/mark-all-read", {}).catch(() => {});
  };

  const handleDismissNotification = useCallback(async (id, e) => {
    if (e) e.stopPropagation();
    setAllNotifications((prev) => {
      const filtered = prev.filter((n) => n.id !== id);
      setNotifications(filtered.slice(0, 5));
      return filtered;
    });
    const target = allNotifications.find((n) => n.id === id);
    if (target && !target.is_read) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    window.dispatchEvent(new CustomEvent("mg:notification-dismissed", { detail: { id } }));
    try {
      await api.apiPost(`notifications/${id}/dismiss`, {});
    } catch (err) {
      logger.error("Navbar dismiss API failed, falling back to localStorage", err);
      saveNavbarDismissedId(id, allNotifIds.current);
    }
  }, [allNotifications]);

  const handleDismissAll = useCallback(async () => {
    setAllNotifications([]);
    setNotifications([]);
    setUnreadCount(0);
    window.dispatchEvent(new CustomEvent("mg:notifications-dismissed-all"));
    try {
      await api.apiPost("notifications/dismiss-all", {});
    } catch (err) {
      logger.error("Dismiss all API failed", err);
      allNotifIds.current.forEach((id) => saveNavbarDismissedId(id, allNotifIds.current));
    }
  }, []);

  const handleGoToNotificationsPage = () => {
    setNotifOpen(false);
    setMobileNotifOpen(false);
    navigate("/account/notifications");
  };

  const navItems = [
    { path: "/", label: t("nav_home"), icon: Home, enabled: true },
    { path: "/courses", label: isAr ? "الكورسات" : "Courses", icon: GraduationCap, enabled: sections.courses },
    { path: "/freelancers", label: isAr ? "المستقلون" : "Freelancers", icon: User, enabled: sections.services },
    { path: "/jobs", label: isAr ? "الوظائف" : "Jobs", icon: Briefcase, enabled: sections.jobs },
    { path: "/blog", label: t("nav_blog"), icon: BookOpen, enabled: true },
  ].filter((item) => item.enabled);

  const getMobileMenuItems = () => {
    const items = navItems.map((item) => ({ ...item, type: "main" }));
    if (isLogged) {
      items.push(
        { path: "/Account", label: isAr ? "حسابي" : "My Account", icon: User, type: "user" }
      );
    }
    return items;
  };

  const NotificationItem = ({ n, index }) => {
    const itemRef = useRef(null);
    const startX = useRef(0);
    const isDragging = useRef(false);
    const hasSwiped = useRef(false);
    const [isDismissing, setIsDismissing] = useState(false);

    const config = NAVBAR_NOTIF_CONFIG[n.type] || NAVBAR_NOTIF_CONFIG.default;
    const IconComponent = config.icon;
    const isUnread = !n.is_read;

    const triggerDismiss = useCallback(() => {
      setIsDismissing(true);
      setTimeout(() => handleDismissNotification(n.id), 350);
    }, [n.id]);

    const handleTouchStart = (e) => {
      startX.current = e.touches[0].clientX;
      isDragging.current = true;
      hasSwiped.current = false;
      if (itemRef.current) itemRef.current.style.transition = "none";
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current) return;
      const diff = e.touches[0].clientX - startX.current;
      if (itemRef.current) {
        itemRef.current.style.transform = `translateX(${diff}px)`;
        const opacity = Math.max(0.2, 1 - Math.abs(diff) / 200);
        itemRef.current.style.opacity = String(opacity);
      }
    };

    const handleTouchEnd = (e) => {
      if (!isDragging.current) return;
      isDragging.current = false;
      const diff = e.changedTouches[0].clientX - startX.current;
      const THRESHOLD = 90;
      if (Math.abs(diff) > THRESHOLD) {
        hasSwiped.current = true;
        const dir = diff > 0 ? 1 : -1;
        if (itemRef.current) {
          itemRef.current.style.transition = "transform 0.3s ease, opacity 0.3s ease";
          itemRef.current.style.transform = `translateX(${dir * 400}px)`;
          itemRef.current.style.opacity = "0";
        }
        triggerDismiss();
      } else {
        if (itemRef.current) {
          itemRef.current.style.transition = "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.3s ease";
          itemRef.current.style.transform = "translateX(0)";
          itemRef.current.style.opacity = "1";
        }
      }
    };

    const translated = translateNotification(n, lang);

    return (
      <div
        ref={itemRef}
        className={`mg-navbar__notification-item ${isUnread ? "mg-navbar__notification-item--unread" : ""} ${isDismissing ? "mg-navbar__notification-item--dismissing" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => { if (!hasSwiped.current) handleNotificationClick(n); }}
        style={{ touchAction: "pan-y", animationDelay: `${index * 60}ms` }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleNotificationClick(n); }}
      >
        <div className="mg-navbar__notification-swipe-bg">
          <EyeOff size={20} strokeWidth={2} />
        </div>

        {/* Icon */}
        <div
          className="mg-navbar__notification-icon"
          style={{ background: `${config.color}18`, color: config.color }}
        >
          <IconComponent size={14} strokeWidth={2} />
        </div>

        {/* Unread dot LTR */}
        {!isAr && isUnread && <span className="mg-navbar__notification-dot" />}

        <div className="mg-navbar__notification-content">
          <div className="mg-navbar__notification-title">{translated.title}</div>
          <div className="mg-navbar__notification-message">{translated.message}</div>
          <div className="mg-navbar__notification-time">{timeAgo(n.created_at, lang)}</div>
        </div>

        {/* Unread dot RTL */}
        {isAr && isUnread && <span className="mg-navbar__notification-dot" />}

        <div className="mg-navbar__notification-item-actions">
          <button
            type="button"
            className="mg-navbar__notification-dismiss-btn"
            onClick={(e) => { e.stopPropagation(); handleDismissNotification(n.id, e); }}
            title={tx("dismiss", "إخفاء", "Dismiss")}
          >
            <X size={11} />
          </button>
        </div>
      </div>
    );
  };

  const NotificationsHeader = ({ isMobile = false }) => (
    <div className={`${isMobile ? 'mg-navbar__mobile-notifications-header' : 'mg-navbar__notifications-header'}`}>
      <div className={isMobile ? 'mg-navbar__mobile-notifications-title' : 'mg-navbar__notifications-header-left'}>
        <span className={isMobile ? '' : 'mg-navbar__notifications-title'}>
          {tx("notifications", "الإشعارات", "Notifications")}
        </span>

      </div>
      <div className="mg-navbar__notifications-header-actions">
        {isMobile ? (
          <>
            {unreadCount > 0 && (
              <button
                type="button"
                className="mg-navbar__notifications-mark"
                onClick={handleMarkAllRead}
                title={tx("mark_all_read", "تحديد الكل كمقروء", "Mark all as read")}
              >
                <CheckCheck size={16} />
              </button>
            )}
            {notifications.length > 0 && (
              <button
                type="button"
                className="mg-navbar__notifications-dismiss-all"
                onClick={handleDismissAll}
                title={tx("dismiss_all", "مسح الكل", "Clear all")}
              >
                <span>{tx("dismiss_all", "مسح الكل", "Clear all")}</span>
              </button>
            )}
            <button
              type="button"
              className="mg-navbar__mobile-notifications-close"
              onClick={() => setMobileNotifOpen(false)}
              aria-label={tx("close", "إغلاق", "Close")}
            >
              <X size={18} />
            </button>
          </>
        ) : (
          <>
            {unreadCount > 0 && (
              <button
                type="button"
                className="mg-navbar__notifications-mark"
                onClick={handleMarkAllRead}
                title={tx("mark_all_read", "تحديد الكل كمقروء", "Mark all as read")}
              >
                <CheckCheck size={13} />
                <span>{tx("mark_all_read", "قراءة الكل", "Mark read")}</span>
              </button>
            )}
            {notifications.length > 0 && (
              <button
                type="button"
                className="mg-navbar__notifications-dismiss-all"
                onClick={handleDismissAll}
                title={tx("dismiss_all", "مسح الكل", "Clear all")}
              >
                <span>{tx("dismiss_all", "مسح الكل", "Clear all")}</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  const NotificationsEmpty = () => (
    <div className="mg-navbar__notifications-empty">
      <Inbox className="mg-navbar__notifications-empty-icon" />
      <p>{tx("no_notifications", "لا يوجد إشعارات", "No notifications")}</p>
      <span className="mg-navbar__notifications-empty-sub">
        {tx("no_notifications_desc", "ستظهر إشعاراتك هنا", "Your notifications will appear here")}
      </span>
    </div>
  );

  const NotificationsLoading = () => (
    <div className="mg-navbar__notifications-loading">
      <span className="mg-navbar__spinner" />
      <p>{tx("loading", "جارِ التحميل...", "Loading...")}</p>
    </div>
  );

  return (
    <>
      {showBanner && (
        <div className="mg-verify-banner" dir={isAr ? "rtl" : "ltr"}>
          <div className="container mg-verify-banner__content">
            <div className="mg-verify-banner__text">
              <AlertCircle size={16} />
              {isAr
                ? "حسابك غير مفعّل — يرجى تفعيل البريد الإلكتروني."
                : "Your account is not verified — please verify your email."}
            </div>
            <div className="mg-verify-banner__actions">
              <button
                type="button"
                className="mg-verify-banner__btn"
                onClick={handleResendVerification}
                disabled={verifySending}
              >
                {verifySending
                  ? isAr ? "جارٍ الإرسال..." : "Sending..."
                  : isAr ? "إعادة إرسال" : "Resend"}
              </button>
              {verifyMsg && (
                <span className="mg-verify-banner__message">
                  <Check size={14} style={{ marginInlineEnd: 4 }} />
                  {verifyMsg}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <nav
        className={`mg-navbar ${isScrolled ? "mg-navbar--scrolled" : ""} ${isOpen ? "mg-navbar--menu-open" : ""}`}
        dir={isAr ? "rtl" : "ltr"}
      >
        <div className="container mg-navbar__container">
          <Link className="mg-navbar__logo" to="/" onClick={closeMenu}>
            <img
              src={logo}
              alt={isAr ? settings?.site_name_ar : settings?.site_name_en}
              className="mg-navbar__logo-img"
            />
          </Link>

          <ul className="mg-navbar__nav">
            {navItems.map((item) => (
              <li key={item.path} className="mg-navbar__nav-item">
                <NavLink
                  className={({ isActive }) => `mg-navbar__nav-link ${isActive ? "mg-navbar__nav-link--active" : ""}`}
                  to={item.path}
                  onClick={closeMenu}
                  end={item.path === "/"}
                >
                  <item.icon className="mg-navbar__nav-icon" /> {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

            <div className="mg-navbar__actions mg-navbar__actions--desktop">
              {isLogged && (
                <div className="mg-navbar__notifications" ref={notifDropdownRef}>
                  <button
                    type="button"
                    className={`mg-navbar__icon-btn ${notifOpen ? "mg-navbar__icon-btn--active" : ""}`}
                    onClick={handleOpenNotifications}
                    aria-label={tx("notifications", "الإشعارات", "Notifications")}
                    title={tx("notifications", "الإشعارات", "Notifications")}
                  >
                    <Bell size={16} className={unreadCount > 0 ? "has-unread" : ""} />
                    {unreadCount > 0 && (
                      <span className="mg-navbar__badge mg-navbar__badge--animated">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </button>
                  {notifOpen && (
                    <div className="mg-navbar__notifications-dropdown">
                      <NotificationsHeader />
                      <div className="mg-navbar__notifications-list">
                        {notifLoading && <NotificationsLoading />}
                        {!notifLoading && notifications.length === 0 && <NotificationsEmpty />}
                        {!notifLoading && notifications.map((n, index) => <NotificationItem key={n.id} n={n} index={index} />)}
                      </div>
                      <div className="mg-navbar__notifications-footer">
                        <button
                          type="button"
                          className="mg-navbar__notifications-view-all"
                          onClick={handleGoToNotificationsPage}
                        >
                          {tx("see_all", "عرض الكل", "See all")}
                          <ChevronRight
                            size={14}
                            style={{ marginInlineStart: 4, transform: isAr ? "rotate(180deg)" : "none" }}
                          />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mg-navbar__divider" />

              {!isLogged ? (
                <>
                  <Link to="/login" onClick={closeMenu} className="mg-navbar__btn mg-navbar__btn--outline">
                    <LogIn size={15} />
                    <span>{isAr ? "تسجيل الدخول" : "Login"}</span>
                  </Link>
                  <Link to="/register" onClick={closeMenu} className="mg-navbar__btn mg-navbar__btn--primary">
                    <UserPlus size={15} />
                    <span>{isAr ? "إنشاء حساب" : "Sign Up"}</span>
                  </Link>
                  <button
                    onClick={toggleLanguage}
                    className="mg-navbar__btn mg-navbar__btn--lang"
                    title={isAr ? "تغيير اللغة" : "Change Language"}
                  >
                    <Globe size={15} />
                    <span>{isAr ? "EN" : "AR"}</span>
                  </button>
                  <ThemeToggle variant="compact" />
                </>
              ) : (
                <>
                  <Link to="/Account" className="mg-navbar__icon-btn" onClick={closeMenu} title={isAr ? "حسابي" : "My Account"}>
                    {showNavAvatar ? (
                      <img
                        key={navAvatarUrl}
                        src={navAvatarUrl}
                        alt={user?.name || "Account"}
                        style={{
                          width: "28px",
                          height: "28px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          const currentSrc = e.currentTarget.src || "";
                          if (currentSrc.includes("/laravel-api/storage/") && !e.currentTarget.dataset.retried) {
                            e.currentTarget.dataset.retried = "true";
                            e.currentTarget.src = currentSrc.replace("/laravel-api/storage/", "/laravel-api/public/storage/");
                            return;
                          }
                          setNavAvatarError(true);
                        }}
                      />
                    ) : (
                      <User size={16} />
                    )}
                  </Link>
                  <div className="mg-navbar__divider" />
                  <button
                    onClick={toggleLanguage}
                    className="mg-navbar__btn mg-navbar__btn--lang"
                    title={isAr ? "تغيير اللغة" : "Change Language"}
                  >
                    <Globe size={15} />
                    <span>{isAr ? "EN" : "AR"}</span>
                  </button>
                  <ThemeToggle variant="compact" />
                  <div className="mg-navbar__divider" />
                  <button
                    className="mg-navbar__btn mg-navbar__btn--logout"
                    onClick={() => { logout(); closeMenu(); }}
                    title={isAr ? "تسجيل الخروج" : "Logout"}
                    aria-label={isAr ? "تسجيل الخروج" : "Logout"}
                  >
                    <LogOut size={16} />
                  </button>
                </>
              )}
            </div>

          <div className="mg-navbar__mobile-header-icons">
            {isLogged && (
              <button
                type="button"
                className="mg-navbar__mobile-header-icon mg-navbar__notifications-trigger"
                onClick={handleOpenMobileNotifications}
                aria-label={tx("notifications", "الإشعارات", "Notifications")}
              >
                <Bell size={16} className={unreadCount > 0 ? "has-unread" : ""} />
                {unreadCount > 0 && (
                  <span className="mg-navbar__badge mg-navbar__badge--animated">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
            )}
            <ThemeToggle variant="circular" />
            <button onClick={toggleLanguage} className="mg-navbar__mobile-header-lang">
              <Globe size={14} />
              <span>{isAr ? "EN" : "AR"}</span>
            </button>
          </div>

          <button
            className={`mg-navbar__toggle ${isOpen ? "mg-navbar__toggle--active" : ""}`}
            onClick={toggleMenu}
            aria-label={isAr ? "القائمة" : "Menu"}
          >
            <span className="mg-navbar__toggle-line" />
            <span className="mg-navbar__toggle-line" />
            <span className="mg-navbar__toggle-line" />
          </button>
        </div>

        {isOpen && (
          <div
            className="mg-navbar__mobile-menu mg-navbar__mobile-menu--open mg-navbar__mobile-menu--dropdown"
            style={{ "--menu-top": `${menuTop}px` }}
            onClick={(e) => { if (e.target === e.currentTarget) closeMenu(); }}
          >
            <div className="mg-navbar__mobile-menu-panel">
              <div className="mg-navbar__mobile-topbar" style={{ justifyContent: 'flex-end', padding: '12px 16px', minHeight: 0 }}>
                <div className="mg-navbar__mobile-actions-left" />
              </div>
              <ul className="mg-navbar__mobile-nav">
                {getMobileMenuItems().map((item, index) => (
                  <li key={`${item.path}-${index}`} className="mg-navbar__mobile-nav-item">
                    {item.external ? (
                      <a href={item.path} className="mg-navbar__mobile-nav-link" onClick={closeMenu}>
                        <item.icon size={18} strokeWidth={2} />
                        <span>{item.label}</span>
                      </a>
                    ) : (
                      <NavLink
                        className={({ isActive }) => `mg-navbar__mobile-nav-link ${isActive ? "mg-navbar__mobile-nav-link--active" : ""}`}
                        to={item.path}
                        onClick={closeMenu}
                        end={item.path === "/"}
                      >
                        <item.icon size={18} strokeWidth={2} />
                        <span>{item.label}</span>
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
              <div className="mg-navbar__mobile-actions">
                {!isLogged ? (
                  <>
                    <Link to="/login" onClick={closeMenu} className="mg-navbar__btn mg-navbar__btn--outline">
                      <LogIn size={16} />
                      <span>{isAr ? "تسجيل الدخول" : "Login"}</span>
                    </Link>
                    <Link to="/register" onClick={closeMenu} className="mg-navbar__btn mg-navbar__btn--primary">
                      <UserPlus size={16} />
                      <span>{isAr ? "إنشاء حساب" : "Sign Up"}</span>
                    </Link>
                  </>
                ) : (
                  <button
                    className="mg-navbar__btn mg-navbar__btn--logout"
                    onClick={() => { logout(); closeMenu(); }}
                  >
                    <LogOut size={16} />
                    <span>{isAr ? "تسجيل الخروج" : "Logout"}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {mobileNotifOpen && (
          <div className="mg-navbar__mobile-notifications-dropdown" ref={mobileNotifRef}>
            <div className="mg-navbar__mobile-notifications-panel">
              <NotificationsHeader isMobile={true} />
              <div className="mg-navbar__mobile-notifications-list">
                {notifLoading && <NotificationsLoading />}
                {!notifLoading && notifications.length === 0 && <NotificationsEmpty />}
                {!notifLoading && notifications.map((n, index) => <NotificationItem key={n.id} n={n} index={index} />)}
              </div>
              <div className="mg-navbar__mobile-notifications-footer">
                <button
                  type="button"
                  className="mg-navbar__mobile-notifications-view-all"
                  onClick={handleGoToNotificationsPage}
                >
                  {tx("see_all", "عرض الكل", "See all")}
                  <ChevronRight size={16} style={{ transform: isAr ? "rotate(180deg)" : "none" }} />
                </button>
              </div>
            </div>
          </div>
        )}

        <audio ref={notificationSoundRef} src="/sounds/notification.mp3" preload="auto" />
      </nav>
    </>
  );
}
